import { useMotionValueEvent, useScroll } from "motion/react";
import { useState } from "react";

interface Props {
  yThreshold?: number;
  sensitivity?: number;
}

/**
 * A React hook that tracks the user's scroll position and hides an element when the user scrolls down past a certain threshold with a specified sensitivity.
 * @param {number} options.yThreshold - The vertical scroll position threshold (in pixels) at which to start hiding the element. Defaults to 300.
 * @param {number} options.sensitivity - The minimum scroll delta (in pixels) required to trigger hide/show. Lower is more sensitive. Defaults to 30.
 * @returns {boolean} - A boolean indicating whether the element should be hidden
 */
export function useHideOnScroll({ yThreshold = 300, sensitivity = 30 }: Props = {}): boolean {
  const [shouldHide, setShouldHide] = useState(false);
  const { scrollY } = useScroll();

  useMotionValueEvent(scrollY, "change", (latest) => {
    const previous = scrollY.getPrevious();
    if (previous === undefined) return;

    const delta = latest - previous;

    if (delta > sensitivity && latest > yThreshold) {
      setShouldHide(true);
    } else if (delta < -sensitivity) {
      setShouldHide(false);
    }
  });

  return shouldHide;
}
